import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { PhaseStatus, PhaseType } from '../phases/types';

export interface StockLocation {
  id: string;
  code: string;
  name: string | null;
  zone: string | null;
  note: string | null;
  active: boolean;
  _count?: { materials: number };
}

// Materiál, jak ho vidí skener (bez cen — ty jsou jen pro finance).
export interface ScanMaterial {
  id: string;
  sku: string;
  name: string;
  unit: string;
  category: string | null;
  stockQty: string;
  minQty: string | null;
  barcode: string | null;
  location: string | null; // staré volné umístění
  archivedAt: string | null;
  stockLocation: { id: string; code: string; name: string | null } | null;
  reservedQty: number;
  availableQty: number;
  incomingQty: number; // objednáno a ještě nedorazilo
  fromOrder?: number; // z příjmu: kolik se spárovalo s objednaným
}

export type ScanResult =
  | { type: 'material'; material: ScanMaterial }
  | { type: 'location'; location: StockLocation; materials: ScanMaterial[] }
  | { type: 'search'; code: string; materials: ScanMaterial[] } // víc materiálů podle názvu
  | { type: 'unknown'; code: string };

export interface IssueTarget {
  id: string;
  type: PhaseType;
  status: PhaseStatus;
  mine: boolean;
  propItemName: string;
  projectCode: string;
}

export const whereLabel = (m: Pick<ScanMaterial, 'stockLocation' | 'location'>) =>
  m.stockLocation ? m.stockLocation.code : m.location ? `${m.location} (staré)` : 'bez pozice';

const LOC = ['stock-locations'] as const;

export function useLocations(includeInactive = false) {
  return useQuery({
    queryKey: [...LOC, includeInactive],
    queryFn: () => api.get<StockLocation[]>(`/api/stock-locations${includeInactive ? '?vse=1' : ''}`),
  });
}

export function useLocationDetail(id: string | null) {
  return useQuery({
    queryKey: [...LOC, 'detail', id],
    queryFn: () => api.get<{ location: StockLocation; materials: ScanMaterial[] }>(`/api/stock-locations/${id}`),
    enabled: !!id,
  });
}

export const locationLabelsUrl = (ids?: string[]) => `/api/stock-locations/labels.pdf${ids && ids.length ? `?ids=${ids.join(',')}` : ''}`;

// Pohyb skladu mění sklad, pozice, ranní frontu i spotřebu na fázích.
function useStockMutation<T, R>(fn: (v: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['materials'] });
      qc.invalidateQueries({ queryKey: LOC });
      qc.invalidateQueries({ queryKey: ['purchases'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['phase-materials'] });
    },
  });
}

export const useCreateLocation = () =>
  useStockMutation((v: { code: string; name?: string; zone?: string; note?: string }) => api.post<StockLocation>('/api/stock-locations', v));

export const useUpdateLocation = () =>
  useStockMutation(({ id, ...data }: { id: string; code?: string; name?: string; zone?: string; note?: string; active?: boolean }) =>
    api.patch<StockLocation>(`/api/stock-locations/${id}`, data));

export const useDeleteLocation = () => useStockMutation((id: string) => api.del(`/api/stock-locations/${id}`));

export const useBulkLocations = () =>
  useStockMutation((items: Array<{ code: string; name?: string; zone?: string }>) =>
    api.post<{ created: number; updated: number }>('/api/stock-locations/bulk', { items }));

export const scanCode = (code: string) => api.get<ScanResult>(`/api/stock/scan?code=${encodeURIComponent(code)}`);

export function useIssueTargets(enabled: boolean) {
  return useQuery({ queryKey: ['stock', 'issue-targets'], queryFn: () => api.get<IssueTarget[]>('/api/stock/issue-targets'), enabled, staleTime: 60_000 });
}

export const useReceive = () =>
  useStockMutation(({ id, ...v }: { id: string; qty: number; unitCost?: number; note?: string }) => api.post<ScanMaterial>(`/api/stock/materials/${id}/receive`, v));

export const useIssue = () =>
  useStockMutation(({ id, ...v }: { id: string; qty: number; phaseId?: string; note?: string }) => api.post<ScanMaterial>(`/api/stock/materials/${id}/issue`, v));

export const useCount = () =>
  useStockMutation(({ id, newQty, note }: { id: string; newQty: number; note?: string }) => api.post<ScanMaterial>(`/api/stock/materials/${id}/count`, { newQty, note }));

export const useCountBatch = () =>
  useStockMutation((v: { items: Array<{ materialId: string; newQty: number }>; note?: string }) =>
    api.post<{ changed: number; unchanged: number }>('/api/stock/count-batch', v));

export const useMove = () =>
  useStockMutation(({ id, locationId }: { id: string; locationId: string | null }) => api.post<ScanMaterial>(`/api/stock/materials/${id}/move`, { locationId }));

export const useAssignBarcode = () =>
  useStockMutation(({ id, barcode }: { id: string; barcode: string }) => api.post<ScanMaterial>(`/api/stock/materials/${id}/barcode`, { barcode }));
